import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { BrowserMultiFormatReader } from '@zxing/library';
import { 
  Box, 
  Button, 
  Typography, 
  Modal, 
  Alert, 
  TextField,
  Tabs,
  Tab,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Backdrop,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { 
  QrCode2, 
  Close, 
  CameraAlt, 
  Numbers,
  CheckCircle,
  Error,
  PhotoCamera,
  Cancel,
  Warning,
  Edit
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PhotoCaptureSection from './PhotoCaptureSection';
import RefusalSection from './RefusalSection';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 800, // Augmenté pour les 3 sections
  maxHeight: '90vh',
  overflow: 'auto',
  position: 'relative', // Important pour l'overlay
}));

// Constante pour le temps minimum d'affichage de l'overlay
const MIN_OVERLAY_DISPLAY_TIME = 1500; // en millisecondes

const QRScannerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 400,
  aspectRatio: '1/1',
  margin: '0 auto',
  position: 'relative',
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  border: `2px solid ${theme.palette.primary.main}`,
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`qr-tabpanel-${index}`}
    aria-labelledby={`qr-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const QRCodeConfirmationModal = ({
  open,
  onClose,
  reservation,
  type, // 'pickup' ou 'return'
  expectedCode, // 🔧 ROADMAP: Code attendu
  qrType = 'pickup', // 🔧 ROADMAP: pickup ou return
  userRole = 'owner', // 🔧 ROADMAP: owner ou renter
  onConfirm,
  onRefuse, // 🔧 ROADMAP: Nouvelle callback refus
  onValidation, // 🔧 ROADMAP: Pour mode embedded
  loading = false,
  embedded = false // 🔧 ROADMAP: Mode embedded
}) => {
  const { t } = useTranslation('qrConfirmationModal');
  const [activeTab, setActiveTab] = useState(0);
  const [manualCode, setManualCode] = useState('');
  const [qrData, setQrData] = useState('');
  const [scannerActive, setScannerActive] = useState(false);
  const [error, setError] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [processingAction, setProcessingAction] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [webcamInstanceKey, setWebcamInstanceKey] = useState(0);
  
  // 🔧 CAMERA SELECTION: États pour la détection et sélection des caméras
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [isEnumeratingCameras, setIsEnumeratingCameras] = useState(false);
  
  // Log pour traquer les changements de scannerActive
  useEffect(() => {
    // console.log('🚨 [QRModal] scannerActive changed to:', scannerActive);
  }, [scannerActive]);
  
  // Log pour traquer les changements de processingAction (état de l'overlay)
  useEffect(() => {
    console.log(`🔄 [QRModal] État processingAction: ${processingAction}`);
  }, [processingAction]);

  // 🔧 ROADMAP: Nouveaux états pour les 3 sections
  const [activeStep, setActiveStep] = useState(0); // 0: Photos, 1: QR Code, 2: Validation
  const [photos, setPhotos] = useState([]);
  const [showRefusal, setShowRefusal] = useState(false);
  const [refusalReason, setRefusalReason] = useState('');
  const [refusalPhotos, setRefusalPhotos] = useState([]);
  
  // 🔧 ROADMAP: États pour la gestion des steps et boutons
  const [photosStepCompleted, setPhotosStepCompleted] = useState(false);
  const [qrStepEnabled, setQrStepEnabled] = useState(false);
  const [qrStepCompleted, setQrStepCompleted] = useState(false);
  
  const webcamRef = useRef(null);
  const codeReader = useRef(new BrowserMultiFormatReader());
  const photoCaptureRef = useRef(null); // 🎯 AMÉLIORATION: Ref pour contrôler PhotoCaptureSection
  const lastScannedTextRef = useRef('');

  const pickPreferredCamera = useCallback((videoDevices) => {
    if (!videoDevices.length) {
      return null;
    }

    const backCamera = videoDevices.find((device) => {
      const label = device.label.toLowerCase();
      return label.includes('back') || label.includes('rear') || label.includes('environment');
    });

    if (backCamera) {
      return backCamera;
    }

    if (videoDevices.length > 1) {
      return videoDevices[videoDevices.length - 1];
    }

    return videoDevices[0];
  }, []);

  // 🔧 CAMERA SELECTION: Fonction pour énumérer les caméras disponibles
  const enumerateCameras = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      console.warn('Camera enumeration not supported');
      return;
    }

    setIsEnumeratingCameras(true);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      console.log('🔍 [QRModal] Available cameras:', videoDevices);
      setAvailableCameras(videoDevices);

      if (videoDevices.length === 0) {
        setCameraError(t('errors.cameraAccess'));
        return;
      }

      setSelectedCameraId((currentCameraId) => {
        const currentCameraStillAvailable = currentCameraId && videoDevices.some((device) => device.deviceId === currentCameraId);

        if (currentCameraStillAvailable) {
          return currentCameraId;
        }

        const defaultCamera = pickPreferredCamera(videoDevices);
        if (!defaultCamera) {
          return '';
        }

        console.log('🔍 [QRModal] Selecting preferred camera:', defaultCamera.label || `Camera ${videoDevices.indexOf(defaultCamera) + 1}`);
        return defaultCamera.deviceId;
      });
    } catch (error) {
      console.error('Error enumerating cameras:', error);
      setCameraError(t('errors.cameraAccess'));
    } finally {
      setIsEnumeratingCameras(false);
    }
  }, [pickPreferredCamera, t]);

  // 🔧 ROADMAP: Configuration selon type et rôle
  const config = {
    pickup: {
      owner: {
        title: t('config.pickup.owner.title'),
        description: t('config.pickup.owner.description'),
        qrLabel: t('config.pickup.owner.qrLabel'),
        confirmLabel: t('config.pickup.owner.confirmLabel'),
        refuseLabel: t('config.pickup.owner.refuseLabel')
      },
      renter: {
        title: t('config.pickup.renter.title'), 
        description: t('config.pickup.renter.description'),
        qrLabel: t('config.pickup.renter.qrLabel'),
        confirmLabel: t('config.pickup.renter.confirmLabel'),
        refuseLabel: t('config.pickup.renter.refuseLabel')
      }
    },
    return: {
      owner: {
        title: t('config.return.owner.title'),
        description: t('config.return.owner.description'),
        qrLabel: t('config.return.owner.qrLabel'),
        confirmLabel: t('config.return.owner.confirmLabel'),
        refuseLabel: t('config.return.owner.refuseLabel')
      },
      renter: {
        title: t('config.return.renter.title'),
        description: t('config.return.renter.description'),
        qrLabel: t('config.return.renter.qrLabel'),
        confirmLabel: t('config.return.renter.confirmLabel'), 
        refuseLabel: t('config.return.renter.refuseLabel')
      }
    }
  };

  const currentConfig = config[qrType]?.[userRole] || config.pickup.owner;

  // 🔧 ROADMAP: Étapes du processus (si pas embedded)
  const steps = [
    { label: t('steps.photos'), icon: <PhotoCamera /> },
    { label: t('steps.qrCode'), icon: <QrCode2 /> },
    { label: t('steps.validation'), icon: <CheckCircle /> }
  ];

  const isPickup = type === 'pickup';
  const title = isPickup ? 'Confirmation Pickup' : 'Confirmation Return';

  useEffect(() => {
    console.log('🔍 [QRModal] useEffect open/activeTab/activeStep:', { open, activeTab, activeStep });
    if (open) {
      // ✅ FIX: N'activer le scanner QR que si on est dans l'étape QR Code (activeStep === 1) ET dans l'onglet scanner (activeTab === 0)
      const shouldActivateScanner = activeStep === 1 && activeTab === 0;
      console.log('🔍 [QRModal] Calcul shouldActivateScanner:', { activeStep, activeTab, shouldActivateScanner });
      setScannerActive(shouldActivateScanner);
    }
  }, [open, activeTab, activeStep]);

  // 🎯 AMÉLIORATION: useEffect séparé pour l'initialisation du modal (seulement à l'ouverture)
  useEffect(() => {
    if (open) {
      console.log('🔍 [QRModal] Initialisation du modal - reset des valeurs');
      setError('');
      setQrData('');
      setManualCode(''); // ✅ Seulement à l'ouverture du modal
      setValidationStatus(null);
      setCameraError('');
      setCameraReady(false);
      lastScannedTextRef.current = '';
    }
  }, [open]); // ✅ Dépendance seulement sur 'open'

  // Nettoyage à la fermeture
  useEffect(() => {
    return () => {
      codeReader.current.reset();
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    console.log('🔍 [QRModal] handleTabChange:', { currentTab: activeTab, newTab: newValue, activeStep });
    setActiveTab(newValue);
    setError('');
    setCameraError('');
    setCameraReady(false);
  };

  const handleQRScan = useCallback((scannedText) => {
    setQrData(scannedText);
    setCameraReady(false);
    
    try {
      const parsedData = JSON.parse(scannedText);
      const isValid = parsedData.reservationId === reservation.id && 
                     (parsedData.type === type || parsedData.type === qrType);
      
      if (isValid) {
        setValidationStatus('valid');
        setScannerActive(false);
        setError('');
        
        // 🔧 ROADMAP: Mode embedded - notifier la validation
        if (embedded && onValidation) {
          onValidation(true, {
            code: parsedData.code,
            type: parsedData.type,
            method: 'qr_code'
          });
        }
      } else {
        setValidationStatus('invalid');
        setError(t('errors.invalidQRForReservation'));
        
        // 🔧 ROADMAP: Mode embedded - notifier échec
        if (embedded && onValidation) {
          onValidation(false, null);
        }
      }
    } catch (e) {
      setValidationStatus('invalid');
      setError(t('errors.invalidQR'));
      
      // 🔧 ROADMAP: Mode embedded - notifier échec
      if (embedded && onValidation) {
        onValidation(false, null);
      }
    }
  }, [embedded, onValidation, qrType, reservation.id, t, type]);

  const handleManualCodeChange = (event) => {
    const value = event.target.value.replace(/\D/g, '').slice(0, 6);
    setManualCode(value);
    
    if (value.length === 6) {
      const isValid = parseInt(value) === expectedCode;
      
      if (isValid) {
        setValidationStatus('valid');
        setError('');
        
        // 🔧 ROADMAP: Mode embedded - notifier la validation
        if (embedded && onValidation) {
          onValidation(true, {
            code: value,
            type: qrType,
            method: 'manual_code'
          });
        }
      } else {
        setValidationStatus('invalid');
        setError(t('errors.incorrectCode'));
        
        // 🔧 ROADMAP: Mode embedded - notifier échec
        if (embedded && onValidation) {
          onValidation(false, null);
        }
      }
    } else {
      setValidationStatus(null);
      setError('');
    }
  };

  // 🔧 ROADMAP: Nouvelle fonction de confirmation avec photos
  const handleConfirm = async () => {
    console.log('🔍 [QRModal] handleConfirm - canConfirm:', canConfirm);
    console.log('🔍 [QRModal] handleConfirm - activeTab:', activeTab);
    console.log('🔍 [QRModal] handleConfirm - qrData:', qrData);
    console.log('🔍 [QRModal] handleConfirm - manualCode:', manualCode);
    console.log('🔍 [QRModal] handleConfirm - validationStatus:', validationStatus);
    
    if (!canConfirm) return;
    
    // Activer l'overlay de chargement immédiatement
    setProcessingAction('confirm');
    // S'assurer que le changement d'état est appliqué avant de continuer
    await new Promise(resolve => setTimeout(resolve, 0));
    
    const startTime = Date.now();
    console.log('🚀 [QRModal] Début du processus de confirmation, overlay affiché');

    try {
      let qrDataToSend;
      
      if (activeTab === 0) {
        // Mode scanner QR
        if (!qrData || qrData.trim() === '') {
          setError(t('errors.noQRScanned'));
          setProcessingAction('');
          return;
        }
        try {
          qrDataToSend = JSON.parse(qrData);
        } catch (parseError) {
          console.error('Error parsing QR data:', parseError);
          setError(t('errors.invalidQR'));
          setProcessingAction('');
          return;
        }
      } else {
        // Mode saisie manuelle
        if (!manualCode || manualCode.length !== 6) {
          setError(t('errors.sixDigitCodeRequired'));
          setProcessingAction('');
          return;
        }
        qrDataToSend = { code: manualCode, type: qrType, method: 'manual_code' };
      }

      console.log('🔍 [QRModal] handleConfirm - qrDataToSend:', qrDataToSend);

      // 🎯 AMÉLIORATION: Restructurer les données selon ce que le backend attend
      const confirmData = {
        reservation_id: reservation.id,
        photos: photos,
        user_role: userRole,
        type: qrType,
        notes: `${qrType} confirmé via modal enhanced`,
        method: activeTab === 0 ? 'qr_code' : 'manual_code'
      };

      // ✅ FIX: Ajouter qrData OU manualCode selon le mode, pas dans un sous-objet
      if (activeTab === 0) {
        // Mode scanner QR - ajouter qrData directement
        confirmData.qrData = qrDataToSend;
      } else {
        // Mode saisie manuelle - ajouter manualCode directement  
        confirmData.manualCode = qrDataToSend.code;
      }

      console.log('🔍 [QRModal] handleConfirm - confirmData final:', confirmData);

      // VRAIE attente de la réponse du backend
      await onConfirm(confirmData);
      
      // Garantir un affichage minimum de l'overlay
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        console.log(`⌛ [QRModal] Attente supplémentaire de ${remaining}ms pour l'overlay`);
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      console.log('✅ [QRModal] Confirmation envoyée avec succès');
      
    } catch (error) {
      console.error('❌ [QRModal] Error confirming:', error);
      setError(t('errors.confirmationError'));
      
      // Garantir un affichage minimum de l'overlay même en cas d'erreur
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      
    } finally {
      // Maintenant on peut désactiver l'overlay
      setProcessingAction('');
    }
  };

  // 🔧 ROADMAP: Nouvelle fonction de refus
  const handleRefusal = async (refusalData) => {
    // Activer l'overlay de chargement immédiatement
    setProcessingAction('refuse');
    // S'assurer que le changement d'état est appliqué avant de continuer
    await new Promise(resolve => setTimeout(resolve, 0));
    
    const startTime = Date.now();
    console.log('🚀 [QRModal] Début du processus de refus, overlay affiché');

    try {
      const fullRefusalData = {
        reservation_id: reservation.id,
        user_role: userRole,
        refusal_reason: refusalData.refusal_reason,
        photos: refusalData.photos,
        type: qrType,
        notes: `Refus via modal enhanced`
      };

      // VRAIE attente de la réponse du backend
      await onRefuse(fullRefusalData);
      
      // Garantir un affichage minimum de l'overlay
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        console.log(`⌛ [QRModal] Attente supplémentaire de ${remaining}ms pour l'overlay`);
        await new Promise(resolve => setTimeout(resolve, remaining));
      }

      console.log('✅ [QRModal] Refus envoyé avec succès');
      
    } catch (error) {
      console.error('❌ [QRModal] Error refusing:', error);
      setError(t('errors.refusalError'));
      
      // Garantir un affichage minimum de l'overlay même en cas d'erreur
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, MIN_OVERLAY_DISPLAY_TIME - elapsed);
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      
    } finally {
      // Maintenant on peut désactiver l'overlay
      setProcessingAction('');
    }
  };

  // 🔧 ROADMAP: Reset modal
  const resetModal = () => {
    setActiveStep(0);
    setPhotos([]);
    setShowRefusal(false);
    setRefusalReason('');
    setRefusalPhotos([]);
    setProcessingAction('');
    setQrData('');
    setManualCode('');
    setValidationStatus(null);
    setError('');
    // 🔧 ROADMAP: Reset des nouveaux états pour les steps
    setPhotosStepCompleted(false);
    setQrStepEnabled(false);
    setQrStepCompleted(false);
    
    // 🔧 CAMERA SELECTION: Reset des états caméra
    setAvailableCameras([]);
    setSelectedCameraId('');
    setIsEnumeratingCameras(false);
    setCameraReady(false);
    setScannerActive(false);
    setWebcamInstanceKey(0);
    lastScannedTextRef.current = '';
    
    // 🎯 AMÉLIORATION: Fermer la caméra au reset du modal
    if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
      console.log('🔍 [QRModal] Fermeture de la caméra au reset du modal');
      photoCaptureRef.current.stopCamera();
    }
  };

  // 🔧 ROADMAP: Fermer modal avec reset
  const handleClose = () => {
    if (loading || Boolean(processingAction)) return;
    resetModal();
    onClose();
  };

  // 🔧 ROADMAP: Handlers pour la gestion des steps
  const handleSkipPhotos = () => {
    console.log('🔍 [QRModal] handleSkipPhotos: passage de l\'étape', activeStep, 'vers 1');
    
    // 🎯 LOGIQUE INTELLIGENTE: 
    // - Si pas de photos → continuer sans photos
    // - Si des photos existent → les garder et continuer avec
    if (photos.length === 0) {
      console.log('🔍 [QRModal] handleSkipPhotos: aucune photo - passage direct');
    } else {
      console.log('🔍 [QRModal] handleSkipPhotos: maintien des', photos.length, 'photos existantes');
    }
    
    // 🎯 AMÉLIORATION: Fermer la caméra quand on passe l'étape photos
    if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
      console.log('🔍 [QRModal] Fermeture de la caméra après saut étape photos');
      photoCaptureRef.current.stopCamera();
    }
    
    setPhotosStepCompleted(true);
    setQrStepEnabled(true);
    setActiveStep(1);
  };

  const handleConfirmPhotos = () => {
    console.log('🔍 [QRModal] handleConfirmPhotos: photos.length =', photos.length);
    if (photos.length > 0) {
      console.log('🔍 [QRModal] handleConfirmPhotos: passage de l\'étape', activeStep, 'vers 1');
      
      // 🎯 AMÉLIORATION: Fermer la caméra quand on confirme les photos
      if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
        console.log('🔍 [QRModal] Fermeture de la caméra après confirmation photos');
        photoCaptureRef.current.stopCamera();
      }
      
      setPhotosStepCompleted(true);
      setQrStepEnabled(true);
      setActiveStep(1);
    }
  };

  // 🎯 NOUVELLE FONCTIONNALITÉ: Permettre de rouvrir l'étape photos pour modification
  const handleEditPhotos = () => {
    console.log('🔍 [QRModal] handleEditPhotos: retour à l\'étape photos pour modification');
    setActiveStep(0);
    setPhotosStepCompleted(false);
    // Garder qrStepEnabled à true pour ne pas perdre le progrès si l'utilisateur revient
  };

  // 🔧 ROADMAP: Effet pour surveiller les photos et activer le bon bouton
  useEffect(() => {
    // Si il y a des photos, le bouton "Confirmer les photos" devient actif
    // Sinon, le bouton "Passer cette étape" reste actif
  }, [photos]);

  // ✅ FIX: Effet centralisé pour gérer l'activation du scanner QR
  useEffect(() => {
    const shouldActivateScanner = open && activeStep === 1 && activeTab === 0 && validationStatus !== 'valid' && !loading && !processingAction;
    console.log('🔍 [QRModal] useEffect activeStep/activeTab change:', { activeStep, activeTab, shouldActivateScanner, validationStatus });
    setScannerActive(shouldActivateScanner);
    if (!shouldActivateScanner) {
      setCameraReady(false);
      codeReader.current.reset();
    }
  }, [open, activeStep, activeTab, validationStatus, loading, processingAction]);

  // 🔧 CAMERA SELECTION: Énumérer les caméras quand le scanner devient actif
  useEffect(() => {
    if (scannerActive && availableCameras.length === 0 && !isEnumeratingCameras) {
      console.log('🔍 [QRModal] Scanner activated, enumerating cameras');
      enumerateCameras();
    }
  }, [scannerActive, availableCameras.length, isEnumeratingCameras, enumerateCameras]);

  useEffect(() => {
    if (!scannerActive || !cameraReady || cameraError) {
      return undefined;
    }

    const video = webcamRef.current?.video;
    if (!video) {
      return undefined;
    }

    let cancelled = false;
    lastScannedTextRef.current = '';

    codeReader.current.reset();
    codeReader.current.decodeFromVideoElementContinuously(video, (result, scanError) => {
      if (cancelled) {
        return;
      }

      if (result) {
        const scannedText = result.getText ? result.getText() : result.text;
        if (scannedText && scannedText !== lastScannedTextRef.current) {
          lastScannedTextRef.current = scannedText;
          handleQRScan(scannedText);
        }
        return;
      }

      if (scanError && scanError.name && scanError.name !== 'NotFoundException') {
        console.warn('QR continuous scan warning:', scanError);
      }
    }).catch((scanError) => {
      if (!cancelled) {
        console.error('Unable to start QR continuous scan:', scanError);
        setCameraError(t('errors.cameraAccess'));
        setCameraReady(false);
      }
    });

    return () => {
      cancelled = true;
      codeReader.current.reset();
    };
  }, [scannerActive, cameraReady, cameraError, handleQRScan, selectedCameraId, t, webcamInstanceKey]);

  // 🔧 ROADMAP: Effet pour marquer l'étape QR comme complétée quand le code est validé
  useEffect(() => {
    if (validationStatus === 'valid') {
      setQrStepCompleted(true);
      setActiveStep(2);
    }
  }, [validationStatus]);

  const canConfirm = (
    (activeTab === 0 && qrData && validationStatus === 'valid') ||
    (activeTab === 1 && manualCode.length === 6 && validationStatus === 'valid')
  ) && photosStepCompleted && qrStepCompleted && !loading && processingAction === '';

  // Log de debug pour canConfirm (optimisé)
  useEffect(() => {
    console.log('🔍 [QRModal] canConfirm evaluation:', {
      activeTab,
      qrData: qrData ? 'present' : 'empty',
      manualCode,
      validationStatus,
      photosStepCompleted,
      qrStepCompleted,
      loading,
      processingAction,
      canConfirm
    });
  }, [activeTab, qrData, manualCode, validationStatus, photosStepCompleted, qrStepCompleted, loading, processingAction]); // ✅ FIX: Supprimé canConfirm pour éviter les re-renders

  const handleCameraError = (error) => {
    console.error('Camera error:', error);
    setCameraError(t('errors.cameraAccess'));
    setCameraReady(false);
  };

  const handleCameraReady = useCallback(async () => {
    console.log('✅ [QRModal] Camera stream ready');
    setCameraError('');
    setCameraReady(true);

    if (!availableCameras.length && !isEnumeratingCameras) {
      await enumerateCameras();
    }
  }, [availableCameras.length, enumerateCameras, isEnumeratingCameras]);

  const handleRetryCamera = useCallback(() => {
    console.log('🔍 [QRModal] handleRetryCamera: réinitialisation complète du scanner');
    setCameraError('');
    setCameraReady(false);
    setValidationStatus(null); // ✅ FIX: Réinitialiser validationStatus pour que le scanner reste actif
    setError('');
    setQrData(''); // ✅ FIX: Réinitialiser qrData pour permettre de rescan
    setScannerActive(true);
    lastScannedTextRef.current = '';
    setWebcamInstanceKey((current) => current + 1);
  }, []);

  const handleCameraSelectionChange = useCallback((event) => {
    const nextCameraId = event.target.value;
    console.log('🔍 [QRModal] Camera changed to:', nextCameraId);
    setSelectedCameraId(nextCameraId);
    setCameraError('');
    setCameraReady(false);
    lastScannedTextRef.current = '';
    setWebcamInstanceKey((current) => current + 1);
  }, []);

  const webcamVideoConstraints = selectedCameraId
    ? {
        deviceId: { exact: selectedCameraId },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: 30 }
      }
    : {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 24, max: 30 }
      };

  // 🔧 ROADMAP: Si mode embedded, retourner seulement la section QR
  if (embedded) {
    return (
      <Box>
        {/* Version embedded simplifiée pour QRConfirmationEnhancedModal */}
        <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 2 }}>
          <Tab icon={<QrCode2 />} label={t('ui.qrScanner')} />
          <Tab icon={<Numbers />} label={t('ui.manualCode')} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {currentConfig.qrLabel}
          </Typography>
          
          {/* 🔧 CAMERA SELECTION: Sélecteur de caméra pour version embedded */}
          {availableCameras.length > 1 && (
            <Box sx={{ mb: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('ui.selectCamera', 'Caméra')}</InputLabel>
                <Select
                  value={selectedCameraId}
                  onChange={handleCameraSelectionChange}
                  label={t('ui.selectCamera', 'Caméra')}
                  disabled={isEnumeratingCameras}
                >
                  {availableCameras.map((camera, index) => (
                    <MenuItem key={camera.deviceId} value={camera.deviceId}>
                      {camera.label || `Cam ${index + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          
          <QRScannerContainer>
            {scannerActive && !cameraError && (
              <>
                {console.log('🔴 [QRModal] Rendering Webcam in embedded mode - scannerActive:', scannerActive, 'cameraError:', cameraError)}
                <Webcam
                  key={`embedded-${selectedCameraId || 'environment'}-${webcamInstanceKey}`}
                  ref={webcamRef}
                  audio={false}
                  width="100%"
                  height="100%"
                  videoConstraints={webcamVideoConstraints}
                  onUserMedia={handleCameraReady}
                onUserMediaError={handleCameraError}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              </>
            )}
            
            {(!scannerActive || cameraError) && (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center"
                height="100%"
                bgcolor="grey.100"
              >
                <CameraAlt sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Button 
                  variant="outlined" 
                  onClick={handleRetryCamera}
                  disabled={loading}
                >
                  {t('ui.activateCamera')}
                </Button>
              </Box>
            )}

            {validationStatus === 'valid' && (
              <Box 
                position="absolute" 
                top={0} 
                left={0} 
                right={0} 
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor="rgba(76, 175, 80, 0.9)"
                color="white"
              >
                <CheckCircle sx={{ fontSize: 64 }} />
              </Box>
            )}
          </QRScannerContainer>

          {validationStatus === 'valid' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('ui.qrValidatedSuccessfully')}
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('ui.enterSixDigitCode')}
          </Typography>
          
          <TextField
            fullWidth
            label={t('ui.sixDigitLabel')}
            value={manualCode}
            onChange={handleManualCodeChange}
            placeholder={t('ui.sixDigitPlaceholder')}
            inputProps={{ 
              maxLength: 6,
              style: { 
                textAlign: 'center', 
                fontSize: '1.5rem',
                letterSpacing: '0.5rem'
              }
            }}
            error={validationStatus === 'invalid'}
            color={validationStatus === 'valid' ? 'success' : 'primary'}
            helperText={
              validationStatus === 'valid' ? t('ui.validCode') :
              validationStatus === 'invalid' ? t('ui.incorrectCode') :
              t('ui.enterCodeDisplayed')
            }
            sx={{ mb: 2 }}
          />

          {validationStatus === 'valid' && (
            <Alert severity="success" icon={<CheckCircle />}>
              {t('ui.correctCode')}
            </Alert>
          )}
        </TabPanel>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // 🔧 ROADMAP: Version complète avec 3 sections
  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      aria-labelledby="qr-confirmation-modal"
      BackdropComponent={Backdrop}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
      }}
    >
      <StyledCard>
        {/* Overlay de progression pendant le traitement */}
        {(loading || processingAction) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              borderRadius: 1, // Même border-radius que la card
              animation: 'fadeIn 0.2s ease-in-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 }
              }
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 4,
                boxShadow: 3,
                textAlign: 'center',
                minWidth: 300
              }}
            >
              <CircularProgress size={60} thickness={4} color="primary" />
              <Box mt={3}>
                <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                  {processingAction === 'confirm' 
                    ? t('progress.confirming', 'Confirmation en cours...') 
                    : processingAction === 'refuse'
                      ? t('progress.refusing', 'Refus en cours...')
                      : t('progress.processing', 'Traitement en cours...')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('progress.pleaseWait', 'Veuillez patienter pendant le traitement de votre demande')}
                </Typography>
                {photos.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('progress.uploadingPhotos', 'Téléchargement de {{count}} photo(s)...', { count: photos.length })}
                  </Typography>
                )}
                <LinearProgress 
                  sx={{ 
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    mt: 2
                  }} 
                />
              </Box>
            </Box>
          </Box>
        )}
        <CardContent>
          {/* En-tête avec configuration */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h5" component="div" fontWeight="bold">
                {currentConfig.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentConfig.description}
              </Typography>
              {reservation && (
                <Chip 
                  label={t('ui.reservationNumber', { id: reservation.id.slice(0, 8) })}
                  size="small" 
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            <IconButton onClick={handleClose} disabled={loading || Boolean(processingAction)}>
              <Close />
            </IconButton>
          </Box>

          {/* Stepper */}
          <Box sx={{ py: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel icon={step.icon}>
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Section 1: Photos avec boutons de contrôle */}
          <Box mb={3}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={2}>
              <PhotoCamera color={activeStep === 0 ? "primary" : "disabled"} />
              {t('sections.photos.title', { type: qrType === 'pickup' ? t('sections.photos.beforeHandover') : t('sections.photos.beforeReturn') })}
              {photosStepCompleted && <CheckCircle color="success" />}
            </Typography>

            {/* Composant Photos */}
            <PhotoCaptureSection
              ref={photoCaptureRef}
              photos={photos}
              onPhotosChange={setPhotos}
              maxPhotos={5}
              readOnly={loading || Boolean(processingAction) || activeStep !== 0}
              photoType={qrType === 'pickup' ? 'equipment_state' : 'return_state'}
              optional={true}
              title=""
              sectionId="pickupPhotos"
            />

            {/* Boutons de contrôle pour l'étape Photos */}
            {activeStep === 0 && !photosStepCompleted && (
              <Box display="flex" gap={2} mt={2} justifyContent="center" flexWrap="wrap">
                {photos.length === 0 ? (
                  // Pas de photos - un seul bouton pour passer
                  <Button
                    variant="outlined"
                    onClick={handleSkipPhotos}
                    disabled={loading || Boolean(processingAction)}
                    size="large"
                    sx={{ minWidth: 150 }}
                  >
                    {t('sections.photos.buttons.continueWithoutPhotos')}
                  </Button>
                ) : (
                  // Des photos existent - options multiples
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        console.log('🔍 [QRModal] Suppression de', photos.length, 'photos');
                        setPhotos([]);
                        // Fermer la caméra si elle est ouverte
                        if (photoCaptureRef.current && photoCaptureRef.current.isShowingCamera()) {
                          photoCaptureRef.current.stopCamera();
                        }
                      }}
                      disabled={loading || Boolean(processingAction)}
                      size="large"
                    >
                      {t('sections.photos.buttons.clearPhotos')}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={handleSkipPhotos}
                      disabled={loading || Boolean(processingAction)}
                      size="large"
                      sx={{ minWidth: 150 }}
                    >
                      {t('ui.continueWithPhotos', { count: photos.length })}
                    </Button>
                    
                    <Button
                      variant="contained"
                      onClick={handleConfirmPhotos}
                      disabled={loading || Boolean(processingAction)}
                      size="large"
                      sx={{ minWidth: 150 }}
                    >
                      {t('sections.finalization.buttons.confirmPhotos', { count: photos.length })}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {photosStepCompleted && (
              <Box>
                <Alert 
                  severity="success" 
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleEditPhotos}
                      startIcon={<Edit />}
                      disabled={loading || Boolean(processingAction)}
                    >
                      {t('ui.modify')}
                    </Button>
                  }
                >
                  {t('sections.photos.stepCompleted', { count: photos.length })}
                </Alert>
              </Box>
            )}
          </Box>

          {/* Section 2: QR Code */}
          <Box mb={3}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={2}>
              <QrCode2 color={qrStepEnabled ? "primary" : "disabled"} />
              {t('sections.qrValidation.title')}
              {validationStatus === 'valid' && <CheckCircle color="success" />}
            </Typography>

            {!qrStepEnabled && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ⏳ {t('alerts.pleaseFinishPhotos')}
                </Typography>
              </Alert>
            )}

            {qrStepEnabled && validationStatus === 'valid' ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {t('ui.qrValidatedSuccessfully')}
                </Typography>
              </Alert>
            ) : qrStepEnabled ? (
              <>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange} 
                  centered 
                  sx={{ mb: 2 }}
                >
                  <Tab 
                    icon={<QrCode2 />} 
                    label={t('ui.qrScanner')} 
                  />
                  <Tab 
                    icon={<Numbers />} 
                    label={t('ui.manualCode')} 
                  />
                </Tabs>

                <TabPanel value={activeTab} index={0}>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {currentConfig.qrLabel}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                    💡 <strong>{t('ui.scanTip')}</strong>
                  </Alert>
                  
                  {/* 🔧 CAMERA SELECTION: Sélecteur de caméra */}
                  {availableCameras.length > 1 && (
                    <Box sx={{ mb: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('ui.selectCamera', 'Sélectionner une caméra')}</InputLabel>
                        <Select
                          value={selectedCameraId}
                          onChange={handleCameraSelectionChange}
                          label={t('ui.selectCamera', 'Sélectionner une caméra')}
                          disabled={loading || Boolean(processingAction) || isEnumeratingCameras}
                        >
                          {availableCameras.map((camera, index) => (
                            <MenuItem key={camera.deviceId} value={camera.deviceId}>
                              {camera.label || `${t('ui.camera', 'Caméra')} ${index + 1}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isEnumeratingCameras && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {t('ui.detectingCameras', 'Détection des caméras...')}
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <QRScannerContainer>
                    {(() => {
                      console.log('🔴 [QRModal] Condition check for main Webcam - scannerActive:', scannerActive, 'cameraError:', cameraError, 'activeStep:', activeStep, 'activeTab:', activeTab);
                      return scannerActive && !cameraError;
                    })() && (
                      <>
                        {console.log('🔴 [QRModal] Rendering main Webcam component')}
                        <Webcam
                          key={`full-${selectedCameraId || 'environment'}-${webcamInstanceKey}`}
                          ref={webcamRef}
                          audio={false}
                          width="100%"
                          height="100%"
                          videoConstraints={webcamVideoConstraints}
                          onUserMedia={handleCameraReady}
                        onUserMediaError={handleCameraError}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      </>
                    )}
                    
                    {(!scannerActive || cameraError) && (
                      <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center"
                        height="100%"
                        bgcolor="grey.100"
                      >
                        <CameraAlt sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                        <Button 
                          variant="outlined" 
                          onClick={handleRetryCamera}
                          disabled={loading || Boolean(processingAction)}
                        >
                          {t('ui.activateCamera')}
                        </Button>
                      </Box>
                    )}

                    {validationStatus === 'invalid' && (
                      <Box 
                        position="absolute" 
                        bottom={10}
                        left={10}
                        right={10}
                        display="flex"
                        flexDirection="column"
                        gap={1}
                      >
                        <Alert severity="error">
                          <Typography variant="body2">
                            ❌ {error || t('errors.invalidQR')}
                          </Typography>
                        </Alert>
                        <Button 
                          variant="contained"
                          color="warning"
                          onClick={handleRetryCamera}
                          disabled={loading || Boolean(processingAction)}
                          fullWidth
                          startIcon={<CameraAlt />}
                        >
                          {t('ui.retryScanning', 'Réessayer')}
                        </Button>
                      </Box>
                    )}
                  </QRScannerContainer>

                  {error && validationStatus !== 'invalid' && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {t('ui.enterSixDigitCode')}
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label={t('ui.sixDigitLabel')}
                    value={manualCode}
                    onChange={handleManualCodeChange}
                    placeholder={t('ui.sixDigitPlaceholder')}
                    inputProps={{ 
                      maxLength: 6,
                      style: { 
                        textAlign: 'center', 
                        fontSize: '1.5rem',
                        letterSpacing: '0.5rem'
                      }
                    }}
                    error={validationStatus === 'invalid'}
                    color={validationStatus === 'valid' ? 'success' : 'primary'}
                    helperText={
                      validationStatus === 'valid' ? t('ui.validCode') :
                      validationStatus === 'invalid' ? t('ui.incorrectCode') :
                      t('ui.enterCodeDisplayed')
                    }
                    sx={{ mb: 2 }}
                  />

                  {validationStatus === 'valid' && (
                    <Alert severity="success" icon={<CheckCircle />}>
                      {t('ui.correctCode')}
                    </Alert>
                  )}
                </TabPanel>
              </>
            ) : null}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Section 3: Actions */}
          <Box mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircle color={qrStepCompleted ? "primary" : "disabled"} />
              {t('sections.finalization.title')}
              {qrStepCompleted && <CheckCircle color="success" />}
            </Typography>

            {!qrStepCompleted && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ⏳ {t('alerts.pleaseValidateQR')}
                </Typography>
              </Alert>
            )}

            {qrStepCompleted && !showRefusal ? (
              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleConfirm}
                  disabled={!canConfirm}
                  size="large"
                  sx={{ minWidth: 150 }}
                >
                  {processingAction === 'confirm' ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {t('ui.processing')}
                    </>
                  ) : (
                    currentConfig.confirmLabel
                  )}
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => setShowRefusal(true)}
                  disabled={loading || Boolean(processingAction)}
                  size="large"
                  sx={{ minWidth: 150, display: 'none' }}
                >
                  {currentConfig.refuseLabel}
                </Button>
              </Box>
            ) : qrStepCompleted ? (
              <RefusalSection
                refusalReason={refusalReason}
                onRefusalReasonChange={setRefusalReason}
                refusalPhotos={refusalPhotos}
                onRefusalPhotosChange={setRefusalPhotos}
                onConfirmRefusal={handleRefusal}
                refusalType={qrType}
                userRole={userRole}
                loading={processingAction === 'refuse'}
                disabled={loading || Boolean(processingAction)}
              />
            ) : null}

            {qrStepCompleted && showRefusal && (
              <Box mt={2} textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() => setShowRefusal(false)}
                  disabled={loading || Boolean(processingAction)}
                >
                  {t('ui.backToOptions')}
                </Button>
              </Box>
            )}
          </Box>

          {/* Erreurs */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Informations statut */}
          {validationStatus === 'valid' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                📝 {t('ui.statusInfo', { count: photos.length })}
              </Typography>
            </Alert>
          )}

          {/* Boutons de bas */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              {qrType.toUpperCase()} • {userRole.toUpperCase()}
            </Typography>
            
            <Button 
              onClick={handleClose} 
              disabled={loading || Boolean(processingAction)}
              variant="outlined"
            >
              {t('ui.close')}
            </Button>
          </Box>
        </CardContent>
      </StyledCard>
    </StyledModal>
  );
};

export default QRCodeConfirmationModal;
